import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Handshake,
  Factory,
  Globe,
  Users,
  Target,
  Sprout,
  Truck,
  Landmark,
  Mail,
  MapPin,
  ExternalLink,
  Menu,
  X,
  ChevronDown,
  Droplets,
  FlaskConical
} from "lucide-react";
import { content, LOGO_URL } from "./data/content";
import TradePage from "./pages/TradePage";
import ProgramPage from "./pages/ProgramPage";
import AdminPage from "./pages/AdminPage";

const iconMap = {
  Handshake: Handshake,
  Users: Users,
  Target: Target,
  Building2: Building2,
  Sprout: Sprout,
  Truck: Truck,
  Factory: Factory,
  Landmark: Landmark
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Home Page Component
function HomeNav({ t, lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Navigation */}
      <nav className={`nav-fixed ${scrolled ? "nav-scrolled" : ""}`} data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Skymoon" className="h-12 w-auto" />
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {Object.entries(t.nav).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => scrollToSection(key)}
                  className="text-sm font-medium text-[#94A3B8] hover:text-[#C5A059] transition-colors"
                  data-testid={`nav-${key}`}
                >
                  {value}
                </button>
              ))}
              <Link
                to="/commerce"
                className="text-sm font-medium text-[#C5A059] hover:text-[#D4AF6A] transition-colors border-l border-white/10 pl-8"
                data-testid="nav-commerce"
              >
                {t.footer.trade}
              </Link>
            </div>

            {/* Language Toggle */}
            <div className="flex items-center gap-4">
              <div className="lang-toggle" data-testid="language-toggle">
                <button
                  onClick={() => setLang("fr")}
                  className={`lang-btn ${lang === "fr" ? "active" : ""}`}
                  data-testid="lang-fr"
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`lang-btn ${lang === "en" ? "active" : ""}`}
                  data-testid="lang-en"
                >
                  EN
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden text-[#F8FAFC] p-2"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[150] bg-[#05080F] pt-24 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {Object.entries(t.nav).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => scrollToSection(key)}
                  className="text-xl font-medium text-[#F8FAFC] hover:text-[#C5A059] text-left py-2 border-b border-white/10"
                  data-testid={`mobile-nav-${key}`}
                >
                  {value}
                </button>
              ))}
              <Link
                to="/commerce"
                className="text-xl font-medium text-[#C5A059] hover:text-[#D4AF6A] text-left py-2 border-b border-white/10"
                data-testid="mobile-nav-commerce"
              >
                {t.footer.trade}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function HeroSection({ t }) {
  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
      <section className="hero-section" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1684610529682-553625a1ffed?crop=entropy&cs=srgb&fm=jpg&q=85)` 
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.p
              variants={fadeInUp}
              className="text-[#C5A059] font-mono text-sm tracking-widest mb-6"
            >
              SKYMOON CONSULTANCY — {t.hero.locations}
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[#F8FAFC] leading-tight mb-6"
              data-testid="hero-title"
            >
              {t.hero.title}
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl md:text-2xl text-[#C5A059] font-light tracking-wide mb-6"
            >
              {t.hero.subtitle}
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#94A3B8] max-w-2xl mb-10"
            >
              {t.hero.description}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection("pillars")}
                className="btn-primary"
                data-testid="hero-cta-primary"
              >
                {t.hero.cta}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="btn-secondary"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </button>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="text-[#C5A059]" size={32} />
        </motion.div>
      </section>
  );
}

function VisionSection({ t }) {
  return (
      <section id="vision" className="section-padding bg-[#0F1420]" data-testid="vision-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
                {t.vision.label}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-8 section-title">
                {t.vision.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#94A3B8] leading-relaxed">
                {t.vision.text}
              </motion.p>
            </div>
            <motion.div variants={fadeInUp} className="grid grid-cols-3 gap-6">
              {t.vision.stats.map((stat, i) => (
                <div key={stat.label} className="text-center p-6 bg-[#161B28] rounded-lg border border-white/5">
                  <div className="text-3xl md:text-4xl font-serif font-bold text-[#C5A059] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-[#94A3B8]">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
  );
}

function PillarsSection({ t }) {
  return (
      <section id="pillars" className="section-padding" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.pillars.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-16 section-title">
              {t.pillars.title}
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {t.pillars.items.map((pillar, i) => (
                <motion.div
                  key={pillar.title}
                  variants={fadeInUp}
                  className="pillar-card p-8 relative"
                  data-testid={`pillar-${i + 1}`}
                >
                  <span className="pillar-number">{pillar.number}</span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-lg mb-6">
                      {i === 0 && <Handshake className="text-[#C5A059]" size={24} />}
                      {i === 1 && <Building2 className="text-[#C5A059]" size={24} />}
                      {i === 2 && <Factory className="text-[#C5A059]" size={24} />}
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-[#F8FAFC] mb-4">
                      {pillar.title}
                    </h3>
                    <p className="text-[#94A3B8] leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function ActionSection({ t }) {
  return (
      <section id="action" className="section-padding bg-[#0F1420]" data-testid="action-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.actionFields.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-16 section-title">
              {t.actionFields.title}
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.actionFields.items.map((item, i) => {
                const IconComponent = iconMap[item.icon];
                return (
                  <motion.div
                    key={item.title}
                    variants={fadeInUp}
                    className="action-item"
                    data-testid={`action-item-${i + 1}`}
                  >
                    <div className="action-icon">
                      <IconComponent size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#F8FAFC] mb-2">{item.title}</h4>
                      <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function CiventiaSection({ t, lang }) {
  return (
      <section id="civentia" className="civentia-section section-padding" data-testid="civentia-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
                {t.civentia.label}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-6xl font-serif font-bold text-gradient-gold mb-4">
                {t.civentia.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-xl text-[#F8FAFC] mb-6">
                {t.civentia.subtitle}
              </motion.p>
              <motion.p variants={fadeInUp} className="text-[#94A3B8] leading-relaxed mb-8">
                {t.civentia.desc}
              </motion.p>
              <motion.ul variants={fadeInUp} className="space-y-4">
                {t.civentia.features.map((feature, i) => (
                  <li key={feature} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-[#C5A059] rounded-full" />
                    <span className="text-[#F8FAFC]">{feature}</span>
                  </li>
                ))}
              </motion.ul>
            </div>
            <motion.div
              variants={fadeInUp}
              className="relative"
            >
              <div className="aspect-square rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1567497063796-7952e455a2a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwdGVjaG5pY2lhbiUyMGFncmljdWx0dXJlJTIwZmllbGQlMjB3b3JrfGVufDB8fHx8MTc3Mzk3NTA5OHww&ixlib=rb-4.1.0&q=85"
                  alt="Techniciens africains travaillant sur l'irrigation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05080F] via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#161B28] border border-[#C5A059]/30 p-6 rounded-lg gold-glow">
                <Factory className="text-[#C5A059] mb-2" size={32} />
                <p className="text-sm text-[#94A3B8]">{lang === "fr" ? "Véhicule Opérationnel" : "Operational Vehicle"}</p>
                <p className="text-[#F8FAFC] font-semibold">SKYMOON</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
  );
}

function FoundersSection({ t }) {
  return (
      <section id="founders" className="section-padding" data-testid="founders-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.founders.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
              {t.founders.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#94A3B8] max-w-2xl mb-16">
              {t.founders.subtitle}
            </motion.p>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {t.founders.team.map((founder, i) => (
                <motion.div
                  key={founder.name}
                  variants={fadeInUp}
                  className="group"
                  data-testid={`founder-${i + 1}`}
                >
                  <div className="founder-card mb-6 aspect-[4/5] overflow-hidden">
                    <img
                      src={i === 0 
                        ? "https://customer-assets.emergentagent.com/job_skymoon-consult/artifacts/nkaj3nzz_IMG_3758.png"
                        : "https://customer-assets.emergentagent.com/job_skymoon-consult/artifacts/siefhwfy_IMG_3755.png"
                      }
                      alt={founder.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="founder-info">
                      <p className="text-[#C5A059] text-sm font-mono tracking-wider mb-1">
                        {founder.specialty}
                      </p>
                      <h3 className="text-2xl font-serif font-bold text-[#F8FAFC]">
                        {founder.name}
                      </h3>
                      <p className="text-[#94A3B8]">{founder.role}</p>
                    </div>
                  </div>
                  <p className="text-[#94A3B8] leading-relaxed mb-4">
                    {founder.bio}
                  </p>
                  <a
                    href={`mailto:${founder.email}`}
                    className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF6A] transition-colors"
                    data-testid={`founder-email-${i + 1}`}
                  >
                    <Mail size={16} />
                    <span className="text-sm">{founder.email}</span>
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function PartnersSection({ t, lang }) {
  return (
      <section id="partners" className="section-padding bg-[#0F1420]" data-testid="partners-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.partners.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
              {t.partners.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#94A3B8] max-w-2xl mb-16">
              {t.partners.subtitle}
            </motion.p>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Partner Card */}
              {t.partners.items.map((partner, i) => (
                <motion.div
                  key={partner.name}
                  variants={fadeInUp}
                  className="bg-[#161B28] border border-white/5 rounded-lg p-8 hover:border-[#C5A059]/30 transition-all duration-500"
                  data-testid={`partner-${i + 1}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-[#C5A059] text-sm font-mono tracking-wider mb-2">
                        {partner.type}
                      </p>
                      <h3 className="text-2xl font-serif font-bold text-[#F8FAFC]">
                        {partner.name}
                      </h3>
                    </div>
                    <Globe className="text-[#C5A059]" size={32} />
                  </div>
                  <p className="text-[#94A3B8] leading-relaxed mb-6">
                    {partner.desc}
                  </p>
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF6A] transition-colors"
                    data-testid={`partner-link-${i + 1}`}
                  >
                    <span className="text-sm">{partner.url.replace("https://", "")}</span>
                    <ExternalLink size={14} />
                  </a>
                </motion.div>
              ))}

              {/* Flagship Program Card */}
              <motion.div
                variants={fadeInUp}
                className="lg:col-span-2 bg-gradient-to-br from-[#1B2A4E] to-[#161B28] border border-[#C5A059]/20 rounded-lg p-8 md:p-12 relative overflow-hidden"
                data-testid="project-flagship"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#C5A059]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Droplets className="text-[#C5A059]" size={20} />
                    <span className="text-[#C5A059] text-sm font-mono tracking-wider">
                      {t.partners.project.status}
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#F8FAFC] mb-4">
                    {t.partners.project.title}
                  </h3>
                  <p className="text-[#94A3B8] leading-relaxed mb-4 max-w-4xl">
                    {t.partners.project.desc}
                  </p>
                  <p className="text-[#94A3B8] leading-relaxed mb-8 max-w-4xl">
                    {t.partners.project.desc2}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-6 mb-8">
                    {t.partners.project.pillars.map((pillar, i) => {
                      const PillarIcon = [Factory, Sprout, FlaskConical, Globe][i];
                      return (
                        <div
                          key={pillar.title}
                          className="flex gap-4 bg-[#0F1420]/60 border border-white/5 rounded-lg p-5"
                          data-testid={`flagship-pillar-${i + 1}`}
                        >
                          <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                            <PillarIcon className="text-[#C5A059]" size={20} />
                          </div>
                          <div>
                            <h4 className="text-[#F8FAFC] font-semibold mb-1">{pillar.title}</h4>
                            <p className="text-sm text-[#94A3B8] leading-relaxed">{pillar.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[#C5A059] font-serif italic mb-8" data-testid="flagship-vision">
                    {t.partners.project.vision}
                  </p>
                  <Link
                    to="/programme"
                    className="btn-primary inline-block"
                    data-testid="flagship-program-link"
                  >
                    {lang === "fr" ? "Découvrir le Programme Complet" : "Discover the Full Program"}
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function ContactSection({ t, lang }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [formStatus, setFormStatus] = useState("idle");

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, { ...form, lang });
      setFormStatus("success");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      console.error("Contact form submission failed:", err);
      setFormStatus("error");
    }
  };

  return (
      <section id="contact" className="section-padding" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16"
          >
            <div>
              <motion.p variants={fadeInUp} className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
                {t.contact.label}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
                {t.contact.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#94A3B8] mb-10">
                {t.contact.subtitle}
              </motion.p>

              <motion.div variants={fadeInUp} className="space-y-6 mb-10">
                {t.contact.headquarters.map((hq, i) => (
                  <div key={hq.city} className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                      <MapPin className="text-[#C5A059]" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-1">{hq.city}</p>
                      <p className="text-[#F8FAFC]">{hq.address}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                    <Mail className="text-[#C5A059]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#94A3B8] mb-1">Email</p>
                    <a href={`mailto:${t.contact.email}`} className="text-[#F8FAFC] hover:text-[#C5A059] transition-colors">
                      {t.contact.email}
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <p className="text-sm text-[#94A3B8] mb-4">{lang === "fr" ? "Domaines de Partenariat" : "Partnership Domains"}</p>
                <div className="flex flex-wrap gap-2">
                  {t.contact.domains.map((domain, i) => (
                    <span key={domain} className="px-4 py-2 bg-[#161B28] border border-white/5 rounded-full text-sm text-[#F8FAFC]">
                      {domain}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp}>
              <form className="space-y-6" data-testid="contact-form" onSubmit={handleFormSubmit}>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.fields.name} *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="contact-input"
                    placeholder={t.contact.fields.name}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.fields.email} *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="contact-input"
                    placeholder={t.contact.fields.email}
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.fields.org}</label>
                  <input
                    type="text"
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    className="contact-input"
                    placeholder={t.contact.fields.org}
                    data-testid="contact-org"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.fields.message} *</label>
                  <textarea
                    rows={5}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="contact-input resize-none"
                    placeholder={t.contact.fields.message}
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {formStatus === "sending" ? t.contact.fields.sending : t.contact.fields.submit}
                </button>
                {formStatus === "success" && (
                  <p className="text-sm text-emerald-400" data-testid="contact-success">
                    {t.contact.fields.success}
                  </p>
                )}
                {formStatus === "error" && (
                  <p className="text-sm text-red-400" data-testid="contact-error">
                    {t.contact.fields.error}
                  </p>
                )}
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>
  );
}

function HomeFooter({ t, lang }) {
  return (
      <footer className="footer py-12 px-6 md:px-12" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Skymoon" className="h-10 w-auto" />
              <div>
                <p className="text-sm text-[#94A3B8]">{t.footer.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link 
                to="/commerce" 
                className="text-sm text-[#64748B] hover:text-[#C5A059] transition-colors"
                data-testid="footer-trade-link"
              >
                {t.footer.trade}
              </Link>
              <Link 
                to="/programme" 
                className="text-sm text-[#64748B] hover:text-[#C5A059] transition-colors"
                data-testid="footer-program-link"
              >
                {lang === "fr" ? "Programme 2035" : "2035 Program"}
              </Link>
              <p className="text-sm text-[#64748B]">
                © {new Date().getFullYear()} Skymoon Consultancy. {t.footer.rights}.
              </p>
            </div>
          </div>
        </div>
      </footer>
  );
}

// Home Page Component
function HomePage() {
  const [lang, setLang] = useState("fr");
  const t = content[lang];

  useEffect(() => {
    document.title = lang === "fr"
      ? "Skymoon Consultancy | Holding d'Investissement & Structuration — Dubai · Dakar"
      : "Skymoon Consultancy | Investment & Structuring Holding — Dubai · Dakar";
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#05080F]">
      <div className="noise-overlay" />
      <HomeNav t={t} lang={lang} setLang={setLang} />
      <HeroSection t={t} />
      <VisionSection t={t} />
      <PillarsSection t={t} />
      <ActionSection t={t} />
      <CiventiaSection t={t} lang={lang} />
      <FoundersSection t={t} />
      <PartnersSection t={t} lang={lang} />
      <ContactSection t={t} lang={lang} />
      <HomeFooter t={t} lang={lang} />
    </div>
  );
}

// Main App with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/commerce" element={<TradePage />} />
        <Route path="/programme" element={<ProgramPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
